import { useState, useEffect } from "react";
import { generateMockup } from "./mockupTemplates";

const API = process.env.NODE_ENV === 'production' ? '' : '';

// ── DRIP SEQUENCE DEFAULTS ─────────────────────────────────────────────────
const DEFAULT_TEMPLATES = [
  {
    day: 0,
    label: "Initial Outreach",
    subject: "Made a mockup for {company}",
    body: `Hi {company} team,

I'm Ethan Hufnagle, a digital marketer based in Mayfield. I build sites for machine shops and manufacturers around Northeast Ohio.

I put together a quick mockup of what a fresh site could look like for {company}. PNG mockup is attached here (lots of placeholder text and imagery right now, don't pay too much attention to that)!

Here's the offer: a clean, one-page site built for your shop, optimized for search engines. $100 to build, $25 a month after that to host it. Google Analytics set up, so you can see who's visiting the site and where they're coming from.

No long contracts. No complicated software to learn. Just a sharp page that works.

If it's something you've been thinking about, give me a call: 330-888-9293.

Ethan Hufnagle
SEMdeck
ethan@semdeck.com`,
  },
  {
    day: 5,
    label: "Follow-up #1",
    subject: "Re: {company}'s website",
    body: `Hi,

Just following up in case my last email got buried.

I'm Ethan — I reached out last week about building a website for {company}. $500–$1,500, done in 30 days, $57/month hosting after that.

If the timing isn't right, no worries. If you have questions, I'm easy to reach — 330-888-9293.

Ethan Hufnagle
SEMdeck
ethan@semdeck.com`,
  },
  {
    day: 12,
    label: "Follow-up #2",
    subject: "Re: {company}'s website",
    body: `Hi,

One last thought before I stop reaching out —

Go to Google and search "{company}." What comes up?

If it's nothing, or an outdated listing, that's what potential customers see too. I set up every site I build with on-page SEO before launch so you show up in local searches. Most clients see organic traffic within 60–90 days.

Site built in 30 days. $500–$1,500. $57/month after that.

330-888-9293 if you want to talk.

Ethan Hufnagle
SEMdeck
ethan@semdeck.com`,
  },
  {
    day: 20,
    label: "Break-up Email",
    subject: "Closing the loop — {company}",
    body: `Hi,

I've reached out a few times so I'll leave you alone after this.

If the timing is ever right — whether that's now or down the road — feel free to reach out.

ethan@semdeck.com | 330-888-9293

Ethan Hufnagle
SEMdeck`,
  },
];

// ── HELPERS ───────────────────────────────────────────────────────────────
function daysSince(dateStr) {
  if (!dateStr) return 999;
  return Math.floor((Date.now() - new Date(dateStr)) / 86400000);
}

function getDripStatus(contact, templates) {
  const sent = contact.emailsSent || 0;
  if (sent >= templates.length) return { label: "Sequence Complete", next: null, ready: false };
  const next = templates[sent];
  const days = daysSince(contact.lastEmailDate);
  const ready = sent === 0 || days >= next.day;
  return { label: next.label, next, ready, daysUntil: Math.max(0, next.day - days) };
}

function cleanPriority(priority) {
  return (priority || '').replace(/[^a-zA-Z]/g, '').toUpperCase() || 'MEDIUM';
}

function fillTemplate(text, company) {
  return text.replace(/\{company\}/g, company);
}

// ── API HELPERS ───────────────────────────────────────────────────────────
async function fetchProspects() {
  const res = await fetch(`${API}/api/sheets?sheet=Prospects`);
  const json = await res.json();
  return (json.data || []).filter(p => {
    const status = (p.outreachStatus || '').toLowerCase().trim();
    const notContacted = !status || status === 'not contacted' || status === 'not started';
    const hasEmail = !!p.email.trim();
    return notContacted && hasEmail;
  });
}

async function fetchCampaign() {
  const res = await fetch(`${API}/api/sheets?sheet=Campaign`);
  const json = await res.json();
  return (json.data || []).filter(r => r.company).map(row => ({
    id: row.id,
    company: row.company,
    email: row.website,        // Col B = Email
    emailsSent: parseInt(row.phone) || 0,  // Col C = Emails Sent
    lastEmailDate: row.address || null,    // Col D = Last Email Date
    status: row.priority || 'active',      // Col E = Status
    notes: row.rating || '',               // Col F = Notes
    openCount: parseInt(row.lastUpdated) || 0,   // Col G = Open Count
    lastOpenedDate: row.ssl || null,             // Col H = Last Opened
  }));
}

async function saveToSheet(sheet, rowIndex, values) {
  await fetch(`${API}/api/sheets`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'update', sheet, rowIndex, values }),
  });
}

async function appendToSheet(sheet, values) {
  await fetch(`${API}/api/sheets`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'append', sheet, values }),
  });
}

async function sendEmail(to, subject, body, attachments, contactId) {
  const res = await fetch(`${API}/api/send-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ to, subject, body, attachment: attachments || null, contactId: contactId || null }),
  });
  if (!res.ok) throw new Error('Failed to send email');
}




// ── STYLES ────────────────────────────────────────────────────────────────
const inputStyle = {
  width: "100%", background: "#0f172a", border: "1px solid #374151",
  borderRadius: "6px", color: "#f9fafb", fontSize: "13px",
  padding: "10px 12px", fontFamily: "inherit", boxSizing: "border-box",
};

function btn(bg, color, primary = false) {
  return {
    background: bg, color, border: "none", borderRadius: "6px",
    padding: primary ? "8px 14px" : "6px 12px",
    fontSize: "12px", fontWeight: 600, cursor: "pointer",
    letterSpacing: "0.02em", whiteSpace: "nowrap",
    fontFamily: "inherit",
  };
}

// ── COMPONENTS ────────────────────────────────────────────────────────────
function PriorityBadge({ priority }) {
  const clean = cleanPriority(priority);
  const colors = { HIGH: "#ef4444", MEDIUM: "#f59e0b", LOW: "#22c55e" };
  return (
    <span style={{ background: colors[clean] || "#6b7280", color: "#fff", fontSize: "10px", fontWeight: 700, letterSpacing: "0.08em", padding: "2px 8px", borderRadius: "3px", textTransform: "uppercase" }}>
      {clean}
    </span>
  );
}

function StatusBadge({ status }) {
  const config = { active: { bg: "#1d4ed8", label: "Active" }, responded: { bg: "#059669", label: "Responded" }, closed: { bg: "#374151", label: "Closed" } };
  const c = config[status] || config.active;
  return <span style={{ background: c.bg, color: "#fff", fontSize: "10px", fontWeight: 700, letterSpacing: "0.08em", padding: "2px 8px", borderRadius: "3px", textTransform: "uppercase" }}>{c.label}</span>;
}

const INDUSTRY_CONFIG = {
  machining:    { bg: "#0c1a2e", color: "#60a5fa", border: "#1e3a5f", label: "⚙ Machining" },
  landscaping:  { bg: "#052e16", color: "#4ade80", border: "#166534", label: "🌿 Landscaping" },
  hvac:         { bg: "#1c1917", color: "#fb923c", border: "#9a3412", label: "🔥 HVAC" },
  roofing:      { bg: "#1e1b2e", color: "#c084fc", border: "#6b21a8", label: "🏠 Roofing" },
  "auto repair":{ bg: "#1a1200", color: "#fbbf24", border: "#92400e", label: "🔧 Auto Repair" },
  plumbing:     { bg: "#0c1e2e", color: "#38bdf8", border: "#0369a1", label: "💧 Plumbing" },
  electrical:   { bg: "#1a1500", color: "#facc15", border: "#854d0e", label: "⚡ Electrical" },
};

function IndustryBadge({ industry }) {
  if (!industry) return null;
  const cfg = INDUSTRY_CONFIG[industry.toLowerCase()] || { bg: "#1f2937", color: "#9ca3af", border: "#374151", label: industry };
  return (
    <span style={{ background: cfg.bg, color: cfg.color, fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", padding: "2px 8px", borderRadius: "3px", textTransform: "uppercase", border: `1px solid ${cfg.border}` }}>
      {cfg.label}
    </span>
  );
}

function ProspectCard({ prospect, onAddToCampaign, onSkip, onGenerateMockup }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{ background: "#111827", border: "1px solid #1f2937", borderRadius: "8px", padding: "16px 20px", marginBottom: "10px" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px", flexWrap: "wrap" }}>
            <span style={{ color: "#f9fafb", fontWeight: 700, fontSize: "15px" }}>{prospect.company}</span>
            <PriorityBadge priority={prospect.priority} />
            <IndustryBadge industry={prospect.industry} />
            {prospect.mockupUrl && <span style={{ background: "#4c1d95", color: "#c4b5fd", fontSize: "10px", fontWeight: 700, padding: "2px 8px", borderRadius: "3px", letterSpacing: "0.06em" }}>MOCKUP ✦</span>}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", fontSize: "12px", color: "#6b7280" }}>
            {prospect.website ? <a href={prospect.website} target="_blank" rel="noopener noreferrer" style={{ color: "#60a5fa", textDecoration: "none" }}>{prospect.website.replace(/https?:\/\//, '')}</a> : <span style={{ color: "#ef4444" }}>No website</span>}
            {prospect.email && <span>📧 {prospect.email}</span>}
            {prospect.phone && <span>📞 {prospect.phone}</span>}
          </div>
          {expanded && <div style={{ marginTop: "10px", fontSize: "12px", color: "#9ca3af", background: "#0f172a", padding: "8px 12px", borderRadius: "6px" }}><strong style={{ color: "#6b7280" }}>Issues:</strong> {prospect.issues || "None identified"}</div>}
        </div>
        <div style={{ display: "flex", gap: "8px", flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <button onClick={() => setExpanded(!expanded)} style={btn("#1f2937", "#d1d5db")}>{expanded ? "Less" : "Details"}</button>
          <button onClick={() => onSkip(prospect.id)} style={btn("#1f2937", "#9ca3af")}>Skip</button>
          <button onClick={() => onGenerateMockup(prospect)} style={btn("#4c1d95", "#c4b5fd")}>Mockup ✦</button>
          <button onClick={() => onAddToCampaign(prospect)} style={btn("#2563eb", "#fff", true)}>+ Add to Campaign</button>
        </div>
      </div>
    </div>
  );
}

function CampaignCard({ contact, templates, onSendEmail, onUpdateStatus }) {
  const drip = getDripStatus(contact, templates);
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState(contact.notes || "");

  return (
    <div style={{ background: "#111827", border: `1px solid ${contact.status === "responded" ? "#065f46" : contact.status === "closed" ? "#1f2937" : "#1e3a5f"}`, borderRadius: "8px", padding: "16px 20px", marginBottom: "10px", opacity: contact.status === "closed" ? 0.6 : 1 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px", flexWrap: "wrap" }}>
            <span style={{ color: "#f9fafb", fontWeight: 700, fontSize: "15px" }}>{contact.company}</span>
            <StatusBadge status={contact.status} />
            {contact.status === "active" && <span style={{ fontSize: "11px", color: drip.ready ? "#4ade80" : "#6b7280" }}>{drip.ready ? `✓ ${drip.label} ready` : `${drip.label} in ${drip.daysUntil}d`}</span>}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", fontSize: "12px", color: "#6b7280" }}>
            <span>✉ {contact.email}</span>
            <span>Sent: <strong style={{ color: "#9ca3af" }}>{contact.emailsSent}/{templates.length}</strong></span>
            {contact.lastEmailDate && <span>Last: {contact.lastEmailDate}</span>}
            {contact.emailsSent > 0 && (
              contact.openCount > 0
                ? (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", background: "#1c1917", border: "1px solid #92400e", borderRadius: "4px", padding: "1px 8px", color: "#fbbf24", fontWeight: 600 }}>
                    ◎ {contact.openCount} open{contact.openCount > 1 ? "s" : ""}
                    {contact.lastOpenedDate && <span style={{ color: "#a8a29e", fontWeight: 400 }}>· {contact.lastOpenedDate}</span>}
                  </span>
                )
                : (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", background: "#111827", border: "1px solid #374151", borderRadius: "4px", padding: "1px 8px", color: "#4b5563", fontWeight: 500 }}>
                    ○ Not opened
                  </span>
                )
            )}
          </div>
          <div style={{ marginTop: "10px", display: "flex", gap: "4px" }}>
            {templates.map((_, i) => (
              <div key={i} style={{ flex: 1, height: "4px", borderRadius: "2px", background: i < contact.emailsSent ? "#2563eb" : i === contact.emailsSent ? (drip.ready ? "#4ade80" : "#374151") : "#1f2937" }} />
            ))}
          </div>
          {showNotes && (
            <div style={{ marginTop: "10px" }}>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add notes..." style={{ ...inputStyle, minHeight: "60px", resize: "vertical" }} />
            </div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "6px", flexShrink: 0 }}>
          {contact.status === "active" && drip.next && (
            drip.ready
              ? <button onClick={() => onSendEmail(contact, drip.next)} style={btn("#2563eb", "#fff", true)}>Send {drip.label}</button>
              : <button onClick={() => onSendEmail(contact, drip.next)} style={btn("#1e3a5f", "#60a5fa", true)}>Send Now ↗</button>
          )}
          <div style={{ display: "flex", gap: "6px" }}>
            <button onClick={() => setShowNotes(!showNotes)} style={btn("#1f2937", "#9ca3af")}>Notes</button>
            {contact.status === "active" && <button onClick={() => onUpdateStatus(contact.id, "responded")} style={btn("#065f46", "#4ade80")}>Responded</button>}
            {contact.status !== "closed" && <button onClick={() => onUpdateStatus(contact.id, "closed")} style={btn("#1f2937", "#6b7280")}>Close</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

function EmailComposer({ contact, template, onSend, onCancel }) {
  const [subject, setSubject] = useState(fillTemplate(template.subject, contact.company));
  const [body, setBody] = useState(fillTemplate(template.body, contact.company));
  const [attachments, setAttachments] = useState([]);
  const allEmails = contact.email.split(/,\s*/).map(e => e.trim()).filter(Boolean);
  const [selectedEmails, setSelectedEmails] = useState(new Set(allEmails));

  function toggleEmail(email) {
    setSelectedEmails(prev => {
      const next = new Set(prev);
      if (next.has(email)) { if (next.size > 1) next.delete(email); }
      else next.add(email);
      return next;
    });
  }

  function handleFileChange(e) {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        setAttachments(prev => {
          // Skip duplicates by filename
          if (prev.some(a => a.filename === file.name)) return prev;
          return [...prev, { data: base64, filename: file.name, mimeType: file.type }];
        });
      };
      reader.readAsDataURL(file);
    });
    e.target.value = ''; // reset so same file can be re-added after removal
  }

  function removeAttachment(filename) {
    setAttachments(prev => prev.filter(a => a.filename !== filename));
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "20px" }}>
      <div style={{ background: "#111827", border: "1px solid #374151", borderRadius: "12px", padding: "28px", width: "100%", maxWidth: "620px", maxHeight: "90vh", overflow: "auto" }}>
        <div style={{ marginBottom: "20px" }}>
          <h3 style={{ color: "#f9fafb", margin: "0 0 10px", fontSize: "16px" }}>{template.label} → {contact.company}</h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
            <span style={{ color: "#6b7280", fontSize: "12px", marginRight: "2px" }}>To:</span>
            {allEmails.map(email => (
              <button key={email} onClick={() => toggleEmail(email)} style={{ display: "inline-flex", alignItems: "center", gap: "5px", background: selectedEmails.has(email) ? "#1e3a5f" : "#1f2937", border: `1px solid ${selectedEmails.has(email) ? "#2563eb" : "#374151"}`, borderRadius: "20px", padding: "3px 10px 3px 8px", cursor: "pointer", fontFamily: "inherit" }}>
                <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: selectedEmails.has(email) ? "#60a5fa" : "#4b5563", flexShrink: 0 }} />
                <span style={{ color: selectedEmails.has(email) ? "#e5e7eb" : "#6b7280", fontSize: "12px" }}>{email}</span>
                {allEmails.length > 1 && <span style={{ color: selectedEmails.has(email) ? "#6b7280" : "#4b5563", fontSize: "11px", marginLeft: "1px" }}>{selectedEmails.has(email) ? "✕" : "+"}</span>}
              </button>
            ))}
          </div>
        </div>
        <div style={{ marginBottom: "14px" }}>
          <label style={{ color: "#9ca3af", fontSize: "12px", display: "block", marginBottom: "6px" }}>Subject</label>
          <input value={subject} onChange={e => setSubject(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ marginBottom: "16px" }}>
          <label style={{ color: "#9ca3af", fontSize: "12px", display: "block", marginBottom: "6px" }}>Body</label>
          <textarea value={body} onChange={e => setBody(e.target.value)} style={{ ...inputStyle, minHeight: "280px", resize: "vertical" }} />
        </div>
        <div style={{ marginBottom: "20px" }}>
          <label style={{ color: "#9ca3af", fontSize: "12px", display: "block", marginBottom: "8px" }}>
            Attachments {attachments.length > 0 && <span style={{ color: "#6b7280" }}>({attachments.length})</span>}
          </label>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {attachments.map(a => (
              <div key={a.filename} style={{ display: "flex", alignItems: "center", gap: "10px", background: "#0f172a", border: "1px solid #1f2937", borderRadius: "6px", padding: "8px 12px" }}>
                <span style={{ fontSize: "15px" }}>📎</span>
                <span style={{ color: "#e5e7eb", fontSize: "13px", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.filename}</span>
                <button onClick={() => removeAttachment(a.filename)} style={{ background: "none", border: "none", color: "#6b7280", cursor: "pointer", fontSize: "14px", padding: "0 4px" }}>✕</button>
              </div>
            ))}
            <label style={{ display: "inline-flex", alignItems: "center", gap: "8px", background: "#1f2937", border: "1px dashed #374151", borderRadius: "6px", padding: "8px 16px", cursor: "pointer", color: "#9ca3af", fontSize: "13px", alignSelf: "flex-start" }}>
              <span>📎</span> Add files
              <input type="file" accept="image/*,.pdf" multiple onChange={handleFileChange} style={{ display: "none" }} />
            </label>
          </div>
        </div>
        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
          <button onClick={onCancel} style={btn("#1f2937", "#9ca3af")}>Cancel</button>
          <button onClick={() => onSend(contact, subject, body, attachments.length ? attachments : null, [...selectedEmails].join(', '))} style={btn("#2563eb", "#fff", true)}>
            Send Email{attachments.length > 0 ? ` + ${attachments.length} file${attachments.length > 1 ? 's' : ''}` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}

function TemplateEditor({ templates, onSave, onCancel }) {
  const [editing, setEditing] = useState(templates.map(t => ({ ...t })));

  function update(i, field, value) {
    setEditing(prev => prev.map((t, idx) => idx === i ? { ...t, [field]: value } : t));
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "20px", overflowY: "auto" }}>
      <div style={{ background: "#111827", border: "1px solid #374151", borderRadius: "12px", padding: "28px", width: "100%", maxWidth: "700px", maxHeight: "90vh", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
          <h3 style={{ color: "#f9fafb", margin: 0, fontSize: "16px" }}>Edit Email Templates</h3>
          <span style={{ color: "#6b7280", fontSize: "12px" }}>Use {"{company}"} as a placeholder</span>
        </div>

        {editing.map((template, i) => (
          <div key={i} style={{ marginBottom: "28px", paddingBottom: "28px", borderBottom: i < editing.length - 1 ? "1px solid #1f2937" : "none" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px" }}>
              <span style={{ background: "#2563eb", color: "#fff", fontSize: "10px", fontWeight: 700, padding: "2px 8px", borderRadius: "3px" }}>EMAIL {i + 1}</span>
              <span style={{ color: "#9ca3af", fontSize: "13px", fontWeight: 600 }}>{template.label}</span>
              <span style={{ color: "#374151", fontSize: "12px" }}>{i === 0 ? "Day 0" : `Day ${template.day}`}</span>
            </div>
            <div style={{ marginBottom: "10px" }}>
              <label style={{ color: "#6b7280", fontSize: "11px", display: "block", marginBottom: "5px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Subject</label>
              <input value={template.subject} onChange={e => update(i, "subject", e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={{ color: "#6b7280", fontSize: "11px", display: "block", marginBottom: "5px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Body</label>
              <textarea value={template.body} onChange={e => update(i, "body", e.target.value)} style={{ ...inputStyle, minHeight: "180px", resize: "vertical" }} />
            </div>
          </div>
        ))}

        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
          <button onClick={onCancel} style={btn("#1f2937", "#9ca3af")}>Cancel</button>
          <button onClick={() => onSave(editing)} style={btn("#2563eb", "#fff", true)}>Save Templates</button>
        </div>
      </div>
    </div>
  );
}

const TEMPLATE_INFO = [
  { num: 1, name: "Dark Industrial",  desc: "Black + gold. Bold, heavy industrial feel.",            accent: "#c9a227", industries: ["machining"] },
  { num: 2, name: "Clean Corporate",  desc: "White + navy. Professional and trustworthy.",            accent: "#1a3a5c", industries: ["machining"] },
  { num: 3, name: "Modern Steel",     desc: "Dark navy + blue. Technical, precision-focused.",        accent: "#2f7fd4", industries: ["machining"] },
  { num: 4, name: "Climate Pro",      desc: "Deep navy + ice blue. Clean, modern HVAC brand.",       accent: "#38bdf8", industries: ["hvac"] },
  { num: 5, name: "Always On",        desc: "Dark charcoal + orange. 24/7 emergency energy.",        accent: "#f97316", industries: ["hvac"] },
  { num: 6, name: "Ridgeline",        desc: "Dark slate + terracotta. Heritage craftsmanship.",       accent: "#e07b54", industries: ["roofing"] },
  { num: 7, name: "Stormguard",       desc: "Near-black + electric yellow. Storm damage specialist.",accent: "#eab308", industries: ["roofing"] },
  { num: 8, name: "Garage Pro",       desc: "Matte black + racing red. Performance shop energy.",    accent: "#dc2626", industries: ["auto repair"] },
  { num: 9, name: "Local Mechanic",   desc: "Cream + navy + gold. Friendly neighborhood shop.",      accent: "#c9a84c", industries: ["auto repair"] },
  { num: 10, name: "Buckeye Green",    desc: "Oak Harbor-inspired landscaping homepage with clean service sections.", accent: "#285f2c", industries: ["landscaping"] },
  { num: 11, name: "Lake Erie Electric", desc: "Navy + yellow Northeast Ohio electrical contractor template.", accent: "#f7b731", industries: ["electrical"] },
];

function MockupModal({ prospect, onClose, onPublished }) {
  const [step, setStep] = useState("pick"); // pick | generating | preview | publishing | done
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [publishedUrl, setPublishedUrl] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  // Cached after first generate so switching templates reuses the same copy/logo
  const [cachedAssets, setCachedAssets] = useState(null);

  async function handleGenerate(templateNum) {
    setSelectedTemplate(templateNum);
    setStep("generating");
    setError(null);

    let logoUrl = cachedAssets?.logoUrl ?? null;
    let scrapedCompany = cachedAssets?.scrapedCompany ?? null;
    let copy = cachedAssets?.copy ?? null;

    try {
    // Only hit the APIs on the first run
    if (!cachedAssets) {
      if (prospect.website) {
        try {
          const res = await fetch(`${API}/api/scrape-logo?url=${encodeURIComponent(prospect.website)}`, {
            signal: AbortSignal.timeout(9000),
          });
          const data = await res.json();
          logoUrl = data.logoDataUrl || data.logoUrl || null;
          scrapedCompany = data.companyName || null;

          try {
            const copyRes = await fetch(`${API}/api/generate-copy`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ pageText: data.pageText || '', company: prospect.company || scrapedCompany, industry: prospect.industry || '' }),
            });
            const copyData = await copyRes.json();
            copy = copyData.copy || null;
          } catch { /* fallback to default copy */ }
        } catch { /* scraping failed, still try copy gen */ }
      }

      // If scraping failed entirely but we have the company name, still generate copy
      if (!copy && (prospect.company || scrapedCompany)) {
        try {
          const copyRes = await fetch(`${API}/api/generate-copy`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ pageText: '', company: prospect.company || scrapedCompany, industry: prospect.industry || '' }),
          });
          const copyData = await copyRes.json();
          copy = copyData.copy || null;
        } catch { /* fallback to default copy */ }
      }

      setCachedAssets({ logoUrl, scrapedCompany, copy });
    }

    const html = generateMockup(templateNum, {
      company: prospect.company || scrapedCompany || "Your Company",
      phone: prospect.phone || "",
      email: prospect.email || "",
      address: prospect.address || "",
      logoUrl,
      copy,
    });

    setPreviewHtml(html);
    const blob = new Blob([html], { type: "text/html" });
    setBlobUrl(URL.createObjectURL(blob));
    setStep("preview");
    } catch (err) {
      setError(err.message || "Failed to generate mockup. Please try again.");
      setStep("pick");
    }
  }

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [blobUrl]);

  async function handlePublish() {
    if (!previewHtml) return;
    setStep("publishing");
    setError(null);
    const slug = prospect.company
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    try {
      const res = await fetch(`${API}/api/publish-mockup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, html: previewHtml }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Publish failed');
      setPublishedUrl(data.url);
      setStep("done");
      onPublished(prospect.id, data.url);
    } catch (err) {
      setError(err.message);
      setStep("preview");
    }
  }

  async function handleScreenshot() {
    if (!previewHtml) return;
    setStep("publishing");
    setError(null);
    try {
      const res = await fetch(`${API}/api/screenshot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ html: previewHtml, filename: prospect.company }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Screenshot failed');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const slug = prospect.company.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      const templateName = (TEMPLATE_INFO.find(t => t.num === selectedTemplate)?.name || 'template').toLowerCase().replace(/\s+/g, '-');
      a.href = url;
      a.download = `${slug}-${templateName}.jpg`;
      a.click();
      URL.revokeObjectURL(url);
      setStep("preview");
    } catch (err) {
      setError(err.message);
      setStep("preview");
    }
  }

  function handleCopy(url) {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.9)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "20px" }}>
      <div style={{ background: "#111827", border: "1px solid #374151", borderRadius: "12px", width: "100%", maxWidth: step === "preview" || step === "done" ? "1000px" : "620px", maxHeight: "90vh", overflow: "auto", display: "flex", flexDirection: "column" }}>

        {/* Header */}
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #1f2937", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div>
            <h3 style={{ color: "#f9fafb", margin: 0, fontSize: "15px", fontWeight: 700 }}>Generate Mockup — {prospect.company}</h3>
            <p style={{ color: "#6b7280", fontSize: "12px", margin: "2px 0 0" }}>
              {step === "pick" && "Choose a template"}
              {step === "generating" && "Scanning website..."}
              {step === "preview" && `Template ${selectedTemplate} preview`}
              {step === "publishing" && "Publishing to web..."}
              {step === "done" && "Published! Share this link with your prospect."}
            </p>
            {error && <p style={{ color: "#ef4444", fontSize: "12px", margin: "4px 0 0" }}>{error}</p>}
          </div>
          <button onClick={onClose} style={btn("#1f2937", "#9ca3af")}>✕ Close</button>
        </div>

        {/* Step: Pick Template */}
        {step === "pick" && (
          <div style={{ padding: "24px" }}>
            {prospect.mockupUrl && (
              <div style={{ background: "#0f172a", border: "1px solid #1e3a5f", borderRadius: "8px", padding: "12px 16px", marginBottom: "20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
                <div>
                  <div style={{ fontSize: "11px", color: "#6b7280", marginBottom: "4px" }}>EXISTING MOCKUP</div>
                  <a href={prospect.mockupUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#60a5fa", fontSize: "13px" }}>{prospect.mockupUrl}</a>
                </div>
                <button onClick={() => handleCopy(prospect.mockupUrl)} style={btn("#1f2937", "#9ca3af")}>Copy Link</button>
              </div>
            )}
            {(() => {
              const industry = (prospect.industry || '').toLowerCase();
              const industryTemplates = TEMPLATE_INFO.filter(t => t.industries.includes(industry));
              const genericTemplates = TEMPLATE_INFO.filter(t => !t.industries.includes(industry));
              const showBoth = industryTemplates.length > 0;

              function TemplateBtn({ t }) {
                return (
                  <button
                    key={t.num}
                    onClick={() => handleGenerate(t.num)}
                    style={{ background: "#0f172a", border: "1px solid #1f2937", borderRadius: "8px", padding: "18px 20px", textAlign: "left", cursor: "pointer", display: "flex", alignItems: "center", gap: "16px", fontFamily: "inherit" }}
                  >
                    <div style={{ width: "14px", height: "40px", background: t.accent, borderRadius: "3px", flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ color: "#f9fafb", fontWeight: 700, fontSize: "14px", marginBottom: "4px" }}>{t.name}</div>
                      <div style={{ color: "#6b7280", fontSize: "12px" }}>{t.desc}</div>
                    </div>
                    <span style={{ color: "#374151", fontSize: "18px" }}>→</span>
                  </button>
                );
              }

              return (
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  {showBoth && (
                    <>
                      <div style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.1em", color: "#6b7280", textTransform: "uppercase", marginBottom: "4px" }}>
                        {industry.charAt(0).toUpperCase() + industry.slice(1)} Templates
                      </div>
                      {industryTemplates.map(t => <TemplateBtn key={t.num} t={t} />)}
                      <div style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.1em", color: "#4b5563", textTransform: "uppercase", margin: "8px 0 4px" }}>
                        Other Templates
                      </div>
                      {genericTemplates.map(t => <TemplateBtn key={t.num} t={t} />)}
                    </>
                  )}
                  {!showBoth && TEMPLATE_INFO.map(t => <TemplateBtn key={t.num} t={t} />)}
                </div>
              );
            })()}
          </div>
        )}

        {/* Step: Generating */}
        {(step === "generating" || step === "publishing") && (
          <div style={{ padding: "60px 24px", textAlign: "center" }}>
            <div style={{ fontSize: "32px", marginBottom: "16px" }}>⚙️</div>
            <div style={{ color: "#9ca3af", fontSize: "14px" }}>
              {step === "generating"
                ? (prospect.website ? `Scanning ${prospect.website.replace(/https?:\/\//, '')} & writing copy...` : "Building mockup...")
                : "Capturing screenshot..."}
            </div>
          </div>
        )}

        {/* Step: Preview */}
        {step === "preview" && blobUrl && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
            <iframe
              src={blobUrl}
              style={{ flex: 1, border: "none", minHeight: "520px", borderRadius: "0 0 0 0" }}
              title="Mockup Preview"
              sandbox="allow-same-origin allow-scripts"
            />
            <div style={{ padding: "16px 24px", borderTop: "1px solid #1f2937", display: "flex", gap: "10px", alignItems: "center", flexShrink: 0, flexWrap: "wrap" }}>
              <button onClick={() => setStep("pick")} style={btn("#1f2937", "#9ca3af")}>← Change Template</button>
              <button onClick={() => window.open(blobUrl, "_blank")} style={btn("#1f2937", "#9ca3af")}>Open in New Tab ↗</button>
              <div style={{ flex: 1 }} />
              <button onClick={handleScreenshot} style={btn("#059669", "#fff", true)}>Download Screenshot ↓</button>
            </div>
          </div>
        )}

        {/* Step: Done */}
        {step === "done" && publishedUrl && (
          <div style={{ padding: "40px 24px" }}>
            <div style={{ background: "#052e16", border: "1px solid #166534", borderRadius: "8px", padding: "20px 24px", marginBottom: "20px" }}>
              <div style={{ fontSize: "11px", color: "#4ade80", fontWeight: 700, letterSpacing: "0.06em", marginBottom: "8px" }}>LIVE URL</div>
              <a href={publishedUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#86efac", fontSize: "14px", wordBreak: "break-all" }}>{publishedUrl}</a>
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button onClick={() => handleCopy(publishedUrl)} style={btn(copied ? "#065f46" : "#1f2937", copied ? "#4ade80" : "#9ca3af")}>
                {copied ? "Copied!" : "Copy Link"}
              </button>
              <button onClick={() => window.open(publishedUrl, "_blank")} style={btn("#1f2937", "#9ca3af")}>Open ↗</button>
              <div style={{ flex: 1 }} />
              <button onClick={() => setStep("pick")} style={btn("#1f2937", "#6b7280")}>New Mockup</button>
              <button onClick={onClose} style={btn("#2563eb", "#fff", true)}>Done</button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ── MAIN APP ──────────────────────────────────────────────────────────────
export default function App() {
  const [tab, setTab] = useState("prospects");
  const [prospects, setProspects] = useState([]);
  const [campaign, setCampaign] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState(() => {
    try {
      const saved = localStorage.getItem("semdeck_templates");
      return saved ? JSON.parse(saved) : DEFAULT_TEMPLATES;
    } catch { return DEFAULT_TEMPLATES; }
  });
  const [composing, setComposing] = useState(null);
  const [editingTemplates, setEditingTemplates] = useState(false);
  const [toast, setToast] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterIndustry, setFilterIndustry] = useState("all");
  const [mockupModal, setMockupModal] = useState(null);

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const [p, c] = await Promise.all([fetchProspects(), fetchCampaign()]);
        setProspects(p);
        setCampaign(c);
      } catch (err) {
        setError('Failed to load data. Check your API connection.');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  function showToast(msg, type = "success") {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }

  function handleSaveTemplates(updated) {
    setTemplates(updated);
    try { localStorage.setItem("semdeck_templates", JSON.stringify(updated)); } catch {}
    setEditingTemplates(false);
    showToast("Templates saved!");
  }

  async function handleAddToCampaign(prospect) {
    if (!prospect.email) { showToast("No email address for this prospect.", "error"); return; }
    setProspects(prev => prev.filter(p => p.id !== prospect.id));
    await appendToSheet('Campaign', [prospect.company, prospect.email, '0', '', 'active', '']);
    // Re-fetch so contacts have real sheet row IDs (needed for saveToSheet + tracking pixel)
    const fresh = await fetchCampaign();
    setCampaign(fresh);
    await saveToSheet('Prospects', prospect.id, [prospect.company, prospect.website, prospect.phone, prospect.address, prospect.priority, prospect.rating, prospect.lastUpdated, prospect.ssl, prospect.mobile, prospect.cms, prospect.issues, prospect.founded, prospect.notes, 'In Campaign', prospect.contactName, prospect.email]);
    showToast(`${prospect.company} added to campaign!`);
    setTab("campaign");
  }

  async function handleSkip(id) {
    setProspects(prev => prev.filter(p => p.id !== id));
    showToast("Prospect skipped.");
    try {
      await fetch(`${API}/api/sheets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'updateCell', rowIndex: id, col: 'N', value: 'Skipped' }),
      });
    } catch (e) {
      console.error('Failed to persist skip:', e);
    }
  }

async function handleConfirmSend(contact, subject, body, attachments, toOverride) {
    try {
      await sendEmail(toOverride || contact.email, subject, body, attachments, contact.id);
      const newCount = contact.emailsSent + 1;
      const today = new Date().toISOString().split("T")[0];
      setCampaign(prev => prev.map(c => c.id === contact.id ? { ...c, emailsSent: newCount, lastEmailDate: today } : c));
      await saveToSheet('Campaign', contact.id, [contact.company, contact.email, String(newCount), today, contact.status, contact.notes || '']);
      setComposing(null);
      showToast(`Email sent to ${contact.company}!`);
    } catch {
      showToast('Failed to send email.', 'error');
    }
  }

  async function handleUpdateStatus(id, status) {
    setCampaign(prev => prev.map(c => c.id === id ? { ...c, status } : c));
    showToast(status === "responded" ? "Marked as responded! 🎉" : "Contact closed.");
    try {
      await fetch(`${API}/api/sheets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'updateCell', sheet: 'Campaign', rowIndex: id, col: 'E', value: status }),
      });
    } catch (e) {
      console.error('Failed to persist status:', e);
    }
  }

  async function handleMockupPublished(prospectId, url) {
    setProspects(prev => prev.map(p => p.id === prospectId ? { ...p, mockupUrl: url } : p));
    const prospect = prospects.find(p => p.id === prospectId);
    if (prospect) {
      await saveToSheet('Prospects', prospectId, [
        prospect.company, prospect.website, prospect.phone, prospect.address,
        prospect.priority, prospect.rating, prospect.lastUpdated, prospect.ssl,
        prospect.mobile, prospect.cms, prospect.issues, prospect.founded,
        prospect.notes, prospect.outreachStatus, prospect.contactName,
        prospect.email, url,
      ]);
    }
    showToast("Mockup published and saved!");
  }

  const activeContacts = campaign.filter(c => c.status === "active");
  const respondedContacts = campaign.filter(c => c.status === "responded");
  const readyToSend = activeContacts.filter(c => getDripStatus(c, templates).ready);
  const filteredCampaign = filterStatus === "all" ? campaign : campaign.filter(c => c.status === filterStatus);
  const filteredProspects = filterIndustry === "all" ? prospects : prospects.filter(p => (p.industry || '').toLowerCase() === filterIndustry);

  return (
    <div style={{ minHeight: "100vh", background: "#030712", fontFamily: "'League Spartan', 'DM Mono', monospace", color: "#f9fafb" }}>

      {/* Header */}
      <div style={{ borderBottom: "1px solid #1f2937", padding: "0 28px", display: "flex", alignItems: "center", justifyContent: "space-between", height: "56px", position: "sticky", top: 0, background: "#030712", zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span style={{ fontSize: "18px", fontWeight: 800, letterSpacing: "-0.02em" }}>SEMDECK</span>
          <span style={{ color: "#374151", fontSize: "12px" }}>/ outreach</span>
        </div>
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          {[
            { id: "prospects", label: "Inbox", count: prospects.length },
            { id: "campaign", label: "Campaign", count: readyToSend.length > 0 ? `${readyToSend.length} ready` : activeContacts.length },
            { id: "responded", label: "Responded", count: respondedContacts.length },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{ background: tab === t.id ? "#1f2937" : "transparent", color: tab === t.id ? "#f9fafb" : "#6b7280", border: "none", borderRadius: "6px", padding: "6px 14px", fontSize: "12px", fontWeight: tab === t.id ? 700 : 400, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: "6px" }}>
              {t.label}
              {t.count !== 0 && <span style={{ background: tab === t.id ? "#2563eb" : "#1f2937", color: "#fff", fontSize: "10px", fontWeight: 700, padding: "1px 6px", borderRadius: "10px" }}>{t.count}</span>}
            </button>
          ))}
          <button onClick={() => setEditingTemplates(true)} style={{ ...btn("#1f2937", "#9ca3af"), marginLeft: "8px" }}>✏️ Templates</button>
        </div>
      </div>

      {loading && (
        <div style={{ textAlign: "center", padding: "80px 20px", color: "#6b7280", fontSize: "13px" }}>
          Loading prospects...
        </div>
      )}

      {error && (
        <div style={{ textAlign: "center", padding: "80px 20px", color: "#ef4444", fontSize: "13px" }}>
          {error}
        </div>
      )}

      {/* Content */}
      <div style={{ maxWidth: "800px", margin: "0 auto", padding: "28px 20px" }}>

        {tab === "prospects" && (
          <div>
            <div style={{ marginBottom: "20px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
              <div>
                <h2 style={{ margin: "0 0 4px", fontSize: "20px", fontWeight: 700 }}>New Prospects</h2>
                <p style={{ margin: 0, color: "#6b7280", fontSize: "13px" }}>{filteredProspects.length} of {prospects.length} companies — review and add to your outreach campaign</p>
              </div>
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                {[
                  { id: "all", label: "All" },
                  { id: "machining", label: "⚙ Machining" },
                  { id: "landscaping", label: "🌿 Landscaping" },
                  { id: "hvac", label: "🔥 HVAC" },
                  { id: "roofing", label: "🏠 Roofing" },
                  { id: "auto repair", label: "🔧 Auto Repair" },
                  { id: "plumbing", label: "💧 Plumbing" },
                  { id: "electrical", label: "⚡ Electrical" },
                ].map(f => (
                  <button key={f.id} onClick={() => setFilterIndustry(f.id)} style={{ ...btn(filterIndustry === f.id ? "#1f2937" : "transparent", filterIndustry === f.id ? "#f9fafb" : "#6b7280"), border: `1px solid ${filterIndustry === f.id ? "#374151" : "transparent"}` }}>{f.label}</button>
                ))}
              </div>
            </div>
            {filteredProspects.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 20px", color: "#374151" }}>
                <div style={{ fontSize: "40px", marginBottom: "12px" }}>✓</div>
                <p style={{ margin: 0 }}>{prospects.length === 0 ? "All prospects reviewed. Run the scanner to find more." : "No prospects match this filter."}</p>
              </div>
            ) : filteredProspects.map(p => <ProspectCard key={p.id} prospect={p} onAddToCampaign={handleAddToCampaign} onSkip={handleSkip} onGenerateMockup={(pr) => setMockupModal(pr)} />)}
          </div>
        )}

        {tab === "campaign" && (
          <div>
            <div style={{ marginBottom: "24px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
              <div>
                <h2 style={{ margin: "0 0 4px", fontSize: "20px", fontWeight: 700 }}>Active Campaign</h2>
                <p style={{ margin: 0, color: "#6b7280", fontSize: "13px" }}>{activeContacts.length} active · {readyToSend.length} ready to send</p>
              </div>
              <button onClick={async () => { const c = await fetchCampaign(); setCampaign(c); showToast("Refreshed"); }} style={btn("#1f2937", "#9ca3af")}>↻ Refresh</button>
              <div style={{ display: "flex", gap: "6px" }}>
                {["all", "active", "closed"].map(s => (
                  <button key={s} onClick={() => setFilterStatus(s)} style={{ ...btn(filterStatus === s ? "#1f2937" : "transparent", filterStatus === s ? "#f9fafb" : "#6b7280"), textTransform: "capitalize" }}>{s}</button>
                ))}
              </div>
            </div>
            <div style={{ background: "#0f172a", border: "1px solid #1f2937", borderRadius: "8px", padding: "14px 18px", marginBottom: "20px", display: "flex", gap: "20px", flexWrap: "wrap" }}>
              {templates.map((t, i) => (
                <div key={i} style={{ fontSize: "11px", color: "#6b7280" }}>
                  <span style={{ color: "#9ca3af", fontWeight: 600 }}>Email {i + 1}</span>
                  <span style={{ margin: "0 6px", color: "#374151" }}>·</span>
                  {t.label}{i > 0 && <span style={{ color: "#374151" }}> (day {t.day})</span>}
                </div>
              ))}
            </div>
            {filteredCampaign.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 20px", color: "#374151" }}>
                <p style={{ margin: 0 }}>No contacts yet. Add prospects from the Inbox tab.</p>
              </div>
            ) : filteredCampaign.map(c => <CampaignCard key={c.id} contact={c} templates={templates} onSendEmail={(contact, template) => setComposing({ contact, template })} onUpdateStatus={handleUpdateStatus} />)}
          </div>
        )}

        {tab === "responded" && (
          <div>
            <div style={{ marginBottom: "24px" }}>
              <h2 style={{ margin: "0 0 4px", fontSize: "20px", fontWeight: 700 }}>Responded</h2>
              <p style={{ margin: 0, color: "#6b7280", fontSize: "13px" }}>{respondedContacts.length} contacts who replied</p>
            </div>
            {respondedContacts.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 20px", color: "#374151" }}>
                <div style={{ fontSize: "40px", marginBottom: "12px" }}>📬</div>
                <p style={{ margin: 0 }}>No responses yet. Keep sending!</p>
              </div>
            ) : respondedContacts.map(c => <CampaignCard key={c.id} contact={c} templates={templates} onSendEmail={(contact, template) => setComposing({ contact, template })} onUpdateStatus={handleUpdateStatus} />)}
          </div>
        )}
      </div>

      {composing && <EmailComposer contact={composing.contact} template={composing.template} onSend={handleConfirmSend} onCancel={() => setComposing(null)} />}
      {editingTemplates && <TemplateEditor templates={templates} onSave={handleSaveTemplates} onCancel={() => setEditingTemplates(false)} />}
      {mockupModal && <MockupModal prospect={mockupModal} onClose={() => setMockupModal(null)} onPublished={handleMockupPublished} />}

      {toast && (
        <div style={{ position: "fixed", bottom: "24px", right: "24px", background: toast.type === "error" ? "#7f1d1d" : "#14532d", border: `1px solid ${toast.type === "error" ? "#ef4444" : "#22c55e"}`, color: "#f9fafb", padding: "12px 18px", borderRadius: "8px", fontSize: "13px", fontWeight: 500, zIndex: 2000 }}>
          {toast.msg}
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=League+Spartan:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        body { margin: 0; font-family: 'League Spartan', sans-serif; font-size: 15px; }
        button:hover { opacity: 0.85; }
        textarea:focus, input:focus { outline: none; border-color: #2563eb !important; }
      `}</style>
    </div>
  );
}
